
import { of as observableOf,  Observable } from 'rxjs'
import * as React from 'react'
import { PHOTO_TYPE, PlannedPost, PostedPost, VIDEO_TYPE } from 'interfaces'
import styles from './PostGridItem.pcss'
import { PostContentTypeIcon } from 'components/Posts/PostContentTypeIcon'
import { FBVideoPlayer, NativeVideoPlayer } from 'components/VideoPlayer'
import BrokenImageIcon from '@mui/icons-material/BrokenImage'
import Tooltip from '@mui/material/Tooltip'
import ProfileAvatar from 'components/ProfileAvatar'
import { useSelector } from 'react-redux'
import { profilesByPpidSelector } from 'services/destinations'
import { RecycleBadge } from '../RecycleBadge'
import { ARTICLE_TYPE, GIF_TYPE, IMAGE_THUMBNAILS_DIR, STATUS_TYPE, getImagesUrlsMap } from 'shared'
import { fontSizesFor } from 'utils/fontSize'
import PlannedActionsMenu from 'components/Posts/PlannedActionsMenu'
import { useSortable } from '@dnd-kit/sortable'
import { createPortal } from 'react-dom'
import { DragOverlay } from '@dnd-kit/core'
import { FormattedMessage } from 'react-intl'
import { format } from 'date-fns'
import PostedActionsMenu from 'components/Posts/PostedActionsMenu'
import Checkbox from '@mui/material/Checkbox'

interface PostGridItemProps {
  post: PlannedPost | PostedPost
  selected: boolean
  className?: string
  onClick?: (post: PlannedPost) => void
  onToggleSelected: (postId: string) => void
  onEdit?: (post: PlannedPost) => void
  onCopy: (post: PlannedPost | PostedPost) => void
  onDelete?: (id: string) => void
  onShareNow?: (post: PlannedPost | PostedPost) => void
  onRequeue?: (id: string, ppid: string) => void
  onRepost?: (post: PostedPost) => void
  onToggleFavorite?: (postId: string, ppid: string, value: boolean) => Observable<any>
  onArchive?: (postId: string, ppid: string) => void
}

const PostGridItem = (props: PostGridItemProps) => {
  const {
    post, className, selected,
    onCopy, onDelete, onEdit, onShareNow, onClick, onToggleSelected, onRepost, onRequeue,
    onToggleFavorite, onArchive
  } = props
  const { ppPageId } = post
  const containerRef = React.useRef<HTMLDivElement>(null)

  const featuredImageUrls = React.useMemo(() => {
    const imageUrl = post.images && post.images[0]
    if (!imageUrl || post.contentType === GIF_TYPE) {
      return null
    }

    const urlArr = imageUrl.split('/')
    urlArr.splice(urlArr.length - 2, 1, IMAGE_THUMBNAILS_DIR)

    const thumb = getImagesUrlsMap([imageUrl])
    const isPPHosted = imageUrl.indexOf('postplanner.com') !== -1
    return {
      original: imageUrl,
      thumb: isPPHosted ? Object.keys(thumb)[0] : imageUrl
    }
  }, [post.images, post.contentType])

  const [imageUrl, setImageUrl] = React.useState(featuredImageUrls?.thumb || '')
  const profiles = useSelector(profilesByPpidSelector)
  const profile = profiles[ppPageId]

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current
    if (!container) {
      return
    }
    if (!post.isEmpty && container.contains(e.target as Node) && onClick) {
      onClick(post as PlannedPost)
    }
  }

  const postType = post.specialType || post.contentType

  const imageStyles = post.isEmpty ? undefined : {
    backgroundImage: `url(${imageUrl})`
  }

  const onImageError = React.useCallback((e: any) => {
    if (featuredImageUrls) {
      if (e.target && (e.target as HTMLImageElement).src !== featuredImageUrls.original) {
        (e.target as HTMLImageElement).src = featuredImageUrls.original
        setImageUrl(featuredImageUrls.original)
      }
    }
  }, [featuredImageUrls])

  const textStyle: any = { ...fontSizesFor(post.status).normal }
  if (post.details?.textBackground) {
    textStyle.backgroundImage = `url(${post.details.textBackground.backgroundUrl})`
    textStyle.fontWeight = 'bold'
    textStyle.color = '#fff'
  }

  const toggleSelected = React.useCallback(() => {
    onToggleSelected(post.id)
  }, [post.id, onToggleSelected])

  const editPost = React.useCallback(() => {
    if (onEdit) {
      onEdit(post as PlannedPost)
    }
  }, [post, onEdit])

  const deletePost = React.useCallback(() => {
    if (onDelete) {
      onDelete(post.id)
    }
  }, [post.id, onDelete])

  const copyPost = React.useCallback(() => {
    if (onCopy) {
      onCopy(post)
    }
  }, [post, onCopy])

  const shareNow = () => {
    if (onShareNow) {
      onShareNow(post)
    }
  }

  const requeue = React.useCallback(() => {
    if (onRequeue) {
      onRequeue(post.id, post.ppPageId)
    }
  }, [onRequeue, post.id, post.ppPageId])

  const repost = React.useCallback(() => {
    if (onRepost) {
      onRepost(post as PostedPost)
    }
  }, [onRepost, post])

  const archive = React.useCallback(() => {
    if (post.isPosted && onArchive) {
      onArchive(post.id, post.ppPageId)
    }
  }, [post.id, post.ppPageId, post.isPosted, onArchive])

  const toggleFavorite = React.useCallback((value: boolean) => {
    if (onToggleFavorite) {
      return onToggleFavorite(post.id, post.ppPageId, value)
    }
    return observableOf(null)
  }, [post.id, post.ppPageId, onToggleFavorite])

  const time = post.time ? format(post.time, 'LLL d') : null

  return (
    <div
      className={`${styles.post} ${post.isEmpty ? styles.empty : ''} ${styles[post.contentType]} ${className || ''}`}
      ref={containerRef}
      data-testid="post-grid-item"
      onClick={handleClick}
    >
      <img style={{ display: 'none' }} onError={onImageError} src={featuredImageUrls?.thumb} />
      {post.bucket && (
        <Tooltip title={`Bucket: ${post.bucket.name}`}>
          <div className={styles['bucket-indicator']} style={{ backgroundColor: post.bucket.color }}></div>
        </Tooltip>
      )}
      <div className={styles['content-box']} style={imageStyles}>
        {post.isEmpty && (
          <span className={styles.nopost}><FormattedMessage id="post.labels.empty-slot" /></span>
        )}
        <div className={styles.header}>
          <div className={styles.left}>
            <ProfileAvatar
              brand={profile.type}
              circle
              size="32px"
              title={profile.name}
              src={profile.image}
              className={styles.avatar}
            />
            <div className={styles['time-box']}>
              <div className={styles.date}>{time}</div>
              <div className={styles.time}>
                {`${post.timeString} ${post.baseTime ? ' ish' : ''}`}
                <RecycleBadge
                  recycle={post.recycle}
                  repeatLength={post.repeatLength}
                  repeatPeriod={post.repeatPeriod}
                  repeatUntil={post.repeatUntil}
                  autoPost={post.autoPost}
                  className={styles.rbadge}
                />
              </div>
            </div>
          </div>
          {!post.isEmpty && (
            <div className={styles.right}>
              {post.isPosted ? (
                <div className={styles['posted-actions']}>
                  <PostedActionsMenu
                    link={(post as PostedPost).realLink}
                    isBucketPost={Boolean(post.bucket)}
                    isArchived={Boolean((post as PostedPost).isArchived)}
                    onCopy={copyPost}
                    onRepost={repost}
                    onRequeue={requeue}
                    onShareNow={props.onShareNow ? shareNow : undefined}
                    buttonClassName={styles.btnPostedActions}
                    isFavorite={Boolean((post as PostedPost).isFavorite)}
                    favButtonOutlineColor="#fff"
                    onArchive={onArchive ? archive : undefined}
                    onToggleFavorite={onToggleFavorite ? toggleFavorite : undefined}
                  />
                  <Checkbox
                    checked={selected}
                    color="primary"
                    className={styles.cb}
                    classes={{ checked: styles.checked }}
                    onChange={toggleSelected}
                  />
                </div>
              ) : (
                <PlannedActionsMenu
                  withMoveControls={false}
                  className={styles.actions}
                  withRecycle={false}
                  withRepeat={false}
                  recycle={post.recycle}
                  repeatLength={post.repeatLength}
                  repeatPeriod={post.repeatPeriod}
                  repeatUntil={post.repeatUntil}
                  selected={selected}
                  virtual={(post as PlannedPost).virtual}
                  checkboxProps={{ className: styles.cb, classes: { checked: styles['cb-checked'] } }}
                  checkboxContainerClassName={styles['cb-box']}
                  virtualLabelClassName={styles['repeat-label']}
                  menuBtnClassName={styles['menu-btn']}
                  onToggleSelected={toggleSelected}
                  onDelete={deletePost}
                  onEdit={editPost}
                  onCopy={copyPost}
                  onShareNow={shareNow}
                />
              )}
            </div>
          )}
        </div>
        {post.contentType === ARTICLE_TYPE && (
          <React.Fragment>
            <div className={styles['article-img']} style={imageStyles}>
              {!featuredImageUrls && (
                <BrokenImageIcon className={styles['icon-broken-img']} />
              )}
            </div>
            <div className={styles.article}>
              {post.linkDomain && (
                <div className={`${styles.domain} text-ellipsis`}>
                  <a key="article-link" href={post.linkUrl} target="_blank" rel="noopener">{post.linkDomain}</a>
                </div>
              )}
              <div className={styles.title}>
                <a target="_blank" rel="noopener" href={post.linkUrl} title={post.linkTitle}>
                  {post.linkTitle}
                </a>
              </div>
            </div>
          </React.Fragment>
        )}
        {post.contentType === STATUS_TYPE && (
          <div className={styles.text} style={textStyle}>{post.status}</div>
        )}
        {post.nativeVideoUrl && (
          <div className={styles['video-box']}>
            <NativeVideoPlayer
              togglePlayOnHover
              videoUrl={post.nativeVideoUrl}
              thumbnailUrl={post.details?.videoThumbnailUrl}
            />
          </div>
        )}
        {post.fbVideoUrl && (
          <FBVideoPlayer
            videoUrl={post.fbVideoUrl}
            videoId={post.id}
            thumbnailUrl={imageUrl}
          />
        )}
        <PostContentTypeIcon type={postType as any} className={styles['post-type-icon']} />
      </div>
    </div>
  )
}

const MemoPostGridItem = React.memo(PostGridItem)
export default MemoPostGridItem

export const DraggablePostGridItem = React.memo((props: PostGridItemProps & { containerClassName: string, disabled: boolean }) => {
  const { attributes, listeners, setDroppableNodeRef, setDraggableNodeRef, isDragging } = useSortable({
    id: props.post.id,
    data: { post: props.post },
    disabled: props.disabled || !props.post.autoPost || props.post.isPosted
  })

  return (
    <div className={`${styles['post-container']} ${props.containerClassName}`} ref={setDroppableNodeRef}>
      <div ref={setDraggableNodeRef} {...attributes} {...listeners} style={{ opacity: isDragging ? '0.5' : '1' }}>
        <MemoPostGridItem {...props} />
      </div>

      {isDragging && createPortal(
        <DragOverlay>
          <MemoPostGridItem {...props} />
        </DragOverlay>,
        document.body
      )}
    </div>
  )
})
